import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import { useAuth } from '@/contexts/auth'
import {
  LayoutDashboard,
  Users,
  RefreshCw,
  Gamepad2,
  CheckSquare,
  BarChart2,
  UserCheck,
  UserCog,
  ClipboardList,
  History,
  Coins,
  Database,
  User,
  Settings,
  Image as ImageIcon,
  KeyRound,
  BookOpen,
  Ticket,
  Ban,
  Network,
  Percent,
  Crown,
  ShieldCheck,
  FlaskConical,
  ListChecks,
  ArrowLeftRight,
  SlidersHorizontal,
} from 'lucide-react'
import { canSeeMenu } from '@nexbit/types'
import type { Dict } from '@/lib/i18n'

interface NavItem {
  href: string
  labelKey: keyof Dict['nav']
  icon: React.ReactNode
  /**
   * Permission this menu needs. Defaults to the path without the /admin/ prefix, which
   * is how every top-level menu is keyed; nested paths must say it explicitly.
   */
  permissionKey?: string
}

/** Menus are grouped so the testing tools read as a separate area, not another report. */
interface NavGroup {
  titleKey: keyof Dict['nav']
  items: NavItem[]
}

const ADMIN_NAV: NavGroup[] = [
  {
    titleKey: 'management',
    items: [
      { href: '/admin',             labelKey: 'dashboard',   icon: <LayoutDashboard className="h-4 w-4" /> },
      { href: '/admin/rounds',      labelKey: 'rounds',      icon: <RefreshCw className="h-4 w-4" /> },
      { href: '/admin/bets',        labelKey: 'bets',        icon: <Coins className="h-4 w-4" /> },
      { href: '/admin/games',       labelKey: 'games',       icon: <Gamepad2 className="h-4 w-4" /> },
      { href: '/admin/draws',       labelKey: 'draws',       icon: <Ticket className="h-4 w-4" /> },
      { href: '/admin/payout-rates',  labelKey: 'payoutRates',  icon: <Percent className="h-4 w-4" /> },
      { href: '/admin/number-limits', labelKey: 'numberLimits', icon: <Ban className="h-4 w-4" /> },
      // Sits next to ปิดรับเลข, not down with บันทึกกิจกรรม: it is the record of this
      // screen's own work, and the question "ใครอั้นเลขนี้ไว้" is asked while looking at
      // the number. The key is the menu, so any number-limits grant opens it — matched by
      // the route, which accepts either action.
      { href: '/admin/number-limit-history', labelKey: 'numberLimitHistory', icon: <History className="h-4 w-4" />, permissionKey: 'number-limits' },
      { href: '/admin/settlements', labelKey: 'settlements', icon: <CheckSquare className="h-4 w-4" /> },
      { href: '/admin/keno-limits', labelKey: 'kenoLimits', icon: <SlidersHorizontal className="h-4 w-4" /> },
      { href: '/admin/partner-wallet', labelKey: 'partnerWallet', icon: <ArrowLeftRight className="h-4 w-4" /> },
      { href: '/admin/reports',     labelKey: 'reports',     icon: <BarChart2 className="h-4 w-4" /> },
      { href: '/admin/masters',     labelKey: 'masters',     icon: <Crown className="h-4 w-4" /> },
      { href: '/admin/providers',   labelKey: 'providers',   icon: <Database className="h-4 w-4" /> },
      { href: '/admin/audit-logs',  labelKey: 'auditLog',    icon: <ClipboardList className="h-4 w-4" /> },
      { href: '/admin/ip-whitelist', labelKey: 'ipWhitelist', icon: <Network className="h-4 w-4" />, permissionKey: 'settings' },
      { href: '/admin/admins',      labelKey: 'admins',      icon: <ShieldCheck className="h-4 w-4" /> },
      { href: '/admin/banners',     labelKey: 'banners',     icon: <ImageIcon className="h-4 w-4" />, permissionKey: 'settings' },
      { href: '/admin/settings',    labelKey: 'settings',    icon: <Settings className="h-4 w-4" /> },
      { href: '/admin/profile',     labelKey: 'profile',     icon: <UserCog className="h-4 w-4" /> },
    ],
  },
  {
    titleKey: 'staging',
    items: [
      // Not /admin/staging: the active-state check treats a parent path as active for its
      // children, which would light up two menu rows at once.
      { href: '/admin/staging/test',    labelKey: 'stagingTest',    icon: <FlaskConical className="h-4 w-4" />, permissionKey: 'staging' },
      { href: '/admin/staging/runs',    labelKey: 'stagingRuns',    icon: <ListChecks className="h-4 w-4" />,   permissionKey: 'staging' },
      { href: '/admin/staging/reports', labelKey: 'stagingReports', icon: <BarChart2 className="h-4 w-4" />,    permissionKey: 'staging' },
    ],
  },
]

// Super admin (permissions=null) sees everything. A limited admin sees dashboard and
// profile always, plus the menus in its permission list. Managing admins is super-only.
const ALWAYS_VISIBLE = new Set(['/admin', '/admin/profile'])

function isAllowed(item: NavItem, permissions: string[] | null): boolean {
  if (permissions === null) return true
  if (item.href === '/admin/admins') return false
  if (ALWAYS_VISIBLE.has(item.href)) return true
  // Any permission inside the menu opens it — an admin granted only draws:settle still
  // needs the หวย page to reach the button.
  return canSeeMenu(permissions, item.permissionKey ?? item.href.replace('/admin/', ''))
}

function visibleAdminNav(permissions: string[] | null): NavGroup[] {
  return ADMIN_NAV
    .map((g) => ({ ...g, items: g.items.filter((i) => isAllowed(i, permissions)) }))
    // A heading with nothing under it reads as a broken menu.
    .filter((g) => g.items.length > 0)
}

const AGENT_NAV: NavGroup[] = [{
  titleKey: 'management',
  items: [
    { href: '/agent',           labelKey: 'dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
    { href: '/agent/members',   labelKey: 'members',   icon: <Users className="h-4 w-4" /> },
    { href: '/agent/reports',   labelKey: 'reports',   icon: <BarChart2 className="h-4 w-4" /> },
    // Same icon as the admin's ปิดรับเลข screen: one vocabulary across both consoles.
    { href: '/agent/number-rules', labelKey: 'myNumberLimits', icon: <Ban className="h-4 w-4" /> },
    // The agent's own history, scoped server-side to its own book.
    { href: '/agent/number-history', labelKey: 'numberLimitHistory', icon: <History className="h-4 w-4" /> },
    { href: '/agent/settings',  labelKey: 'settings',  icon: <Settings className="h-4 w-4" /> },
    { href: '/agent/keys',      labelKey: 'keys',      icon: <KeyRound className="h-4 w-4" /> },
    { href: '/agent/docs',      labelKey: 'docs',      icon: <BookOpen className="h-4 w-4" /> },
    { href: '/agent/profile',   labelKey: 'profile',   icon: <User className="h-4 w-4" /> },
  ],
}]

// Master manages agents (and drills into an agent to view its members read-only);
// there is deliberately no flat member menu — members belong to agents.
const MASTER_NAV: NavGroup[] = [{
  titleKey: 'management',
  items: [
    { href: '/master',          labelKey: 'dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
    { href: '/master/agents',   labelKey: 'agents',    icon: <UserCheck className="h-4 w-4" /> },
    { href: '/master/reports',  labelKey: 'reports',   icon: <BarChart2 className="h-4 w-4" /> },
  ],
}]

function NavLink({
  href,
  labelKey,
  icon,
  onClick,
}: NavItem & { onClick?: () => void }) {
  const t = useT()
  const { pathname } = useLocation()
  const isRoot = href === '/admin' || href === '/agent' || href === '/master'
  const isActive = pathname === href || (!isRoot && pathname.startsWith(href + '/'))

  return (
    <Link
      to={href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'group relative flex items-center gap-2.5 py-2 pr-3 pl-4 rounded-r-[var(--radius)] text-sm transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'before:absolute before:left-0 before:top-1 before:bottom-1 before:w-0.5 before:rounded-full before:transition-all before:duration-150',
        isActive
          ? 'before:bg-primary bg-primary/8 text-primary font-semibold'
          : 'before:bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <span className={cn('shrink-0 transition-colors', isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')}>
        {icon}
      </span>
      <span className="truncate">{t.nav[labelKey]}</span>
    </Link>
  )
}

interface SidebarProps {
  onClose?: () => void
}

export function Sidebar({ onClose }: SidebarProps) {
  const t = useT()
  const { isAdmin, isMaster, permissions } = useAuth()
  const groups = isAdmin ? visibleAdminNav(permissions) : isMaster ? MASTER_NAV : AGENT_NAV
  const consoleLabel = isAdmin ? t.common.adminConsole : isMaster ? t.common.masterConsole : t.common.agentConsole

  return (
    <nav
      aria-label="Navigation"
      className="flex flex-col h-full w-56 border-r border-sidebar-border bg-sidebar"
    >
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-sidebar-border">
        <div className="h-7 w-7 flex items-center justify-center shrink-0">
          <img src="/favicon.png" alt="" className="h-7 w-7 object-contain" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-semibold leading-none tracking-tight">Lotmatic</span>
          <span className="text-xs text-muted-foreground leading-none mt-0.5">
            {consoleLabel}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-0.5 px-2 py-3 flex-1 overflow-y-auto">
        {groups.map((group) => (
          <div key={group.titleKey} className="flex flex-col gap-0.5 first:mt-0 mt-3">
            <p className="px-4 pb-1.5 pt-0.5 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              {t.nav[group.titleKey]}
            </p>
            {group.items.map((item) => (
              <NavLink key={item.href} {...item} onClick={onClose} />
            ))}
          </div>
        ))}
      </div>

      <div className="px-5 py-3 border-t border-sidebar-border">
        <p className="text-xs text-muted-foreground/40 font-mono">v0.1.0</p>
      </div>
    </nav>
  )
}
